/**
 * Email Classification Workflow
 *
 * Automatically classifies incoming emails using AI:
 * 1. Analyzes email content (spam, advertisement, work, personal)
 * 2. For work emails, tries to match the sender to existing clients
 * 3. Creates appropriate notifications with badges and actions
 *
 * Notification types:
 * - Work email from known client → "Nuova email importante" (high priority)
 * - Work email, client not found → Asks to create client
 * - Advertisement → "Pubblicità" badge
 * - Spam → "Possibile spam" badge
 */

import type { EngineWorkflow } from '../types';

/**
 * Pre-configured email classification workflow
 */
export const emailClassificationWorkflow: EngineWorkflow = {
  id: 'wf-email-classification',
  name: 'Classificazione Email Automatica',
  description: 'Classifica le email ricevute usando AI e notifica in base al tipo (lavoro, spam, pubblicità)',
  trigger: {
    eventType: 'email.received',
  },
  steps: [
    // Step 1: Classify email using AI
    {
      id: 'classify',
      name: 'Classifica email con AI',
      type: 'email_classify',
      config: {
        subject: '{{data.subject}}',
        body: '{{data.body}}',
        from: '{{data.from}}',
        fromName: '{{data.fromName}}',
      },
      next: ['check-classification'],
      onError: 'continue',
      retryCount: 1,
      timeout: 30000,
    },

    // Step 2: Branch based on classification
    {
      id: 'check-classification',
      name: 'Verifica tipo email',
      type: 'condition',
      config: {
        field: 'isWorkEmail',
        operator: 'eq',
        value: true,
        trueSteps: ['match-client'],
        falseSteps: ['check-spam'],
      },
      next: [],
      onError: 'continue',
      retryCount: 0,
      timeout: 5000,
    },

    // Step 3a: Check if spam
    {
      id: 'check-spam',
      name: 'Verifica se spam',
      type: 'condition',
      config: {
        field: 'isSpam',
        operator: 'eq',
        value: true,
        trueSteps: ['notify-spam'],
        falseSteps: ['check-advertisement'],
      },
      next: [],
      onError: 'continue',
      retryCount: 0,
      timeout: 5000,
    },

    // Step 3b: Check if advertisement
    {
      id: 'check-advertisement',
      name: 'Verifica se pubblicità',
      type: 'condition',
      config: {
        field: 'isAdvertisement',
        operator: 'eq',
        value: true,
        trueSteps: ['notify-advertisement'],
        falseSteps: ['notify-generic'],
      },
      next: [],
      onError: 'continue',
      retryCount: 0,
      timeout: 5000,
    },

    // Step 4: Match client (for work emails)
    {
      id: 'match-client',
      name: 'Cerca cliente',
      type: 'client_match',
      config: {
        senderEmail: '{{emailClassification.senderEmail}}',
        senderDomain: '{{emailClassification.senderDomain}}',
        suggestedCompanyName: '{{emailClassification.suggestedCompanyName}}',
      },
      next: ['check-client-found'],
      onError: 'continue',
      retryCount: 0,
      timeout: 10000,
    },

    // Step 5: Branch based on client match
    {
      id: 'check-client-found',
      name: 'Cliente trovato?',
      type: 'condition',
      config: {
        field: 'clientFound',
        operator: 'eq',
        value: true,
        trueSteps: ['notify-important'],
        falseSteps: ['notify-unknown-client'],
      },
      next: [],
      onError: 'continue',
      retryCount: 0,
      timeout: 5000,
    },

    // =======================================================================
    // NOTIFICATION STEPS
    // =======================================================================

    // Notification: Important work email from known client
    {
      id: 'notify-important',
      name: 'Notifica email importante',
      type: 'smart_notify',
      config: {
        title: 'Email da {{clientName}}',
        message: '{{data.subject}}',
        badge: '⭐ Nuova email importante',
        priority: 'high',
        icon: 'mail-star',
        notifType: 'new_email',
        actions: [
          {
            id: 'open',
            label: 'Apri',
            variant: 'primary',
            handler: 'openEmail',
            payload: { emailId: '{{entityId}}' },
          },
          {
            id: 'dismiss',
            label: 'Ignora',
            variant: 'secondary',
            handler: 'dismiss',
          },
        ],
        metadata: {
          clientId: '{{clientId}}',
          clientName: '{{clientName}}',
          company: '{{clientCompany}}',
          classification: 'work',
          senderEmail: '{{emailClassification.senderEmail}}',
        },
      },
      next: [],
      onError: 'continue',
      retryCount: 0,
      timeout: 5000,
    },

    // Notification: Work email but client not found — ask to create
    {
      id: 'notify-unknown-client',
      name: 'Notifica cliente non trovato',
      type: 'smart_notify',
      config: {
        title: 'Email di lavoro',
        message: 'Da: {{emailClassification.senderName}} ({{emailClassification.senderEmail}})\n{{data.subject}}',
        badge: '📧 Email di lavoro',
        priority: 'normal',
        icon: 'mail-question',
        notifType: 'new_email',
        actions: [
          {
            id: 'create-client',
            label: 'Crea cliente',
            variant: 'primary',
            handler: 'createClientFromEmail',
            payload: {
              email: '{{emailClassification.senderEmail}}',
              name: '{{emailClassification.senderName}}',
              company: '{{emailClassification.suggestedCompanyName}}',
            },
          },
          {
            id: 'open',
            label: 'Apri email',
            variant: 'secondary',
            handler: 'openEmail',
            payload: { emailId: '{{entityId}}' },
          },
          {
            id: 'dismiss',
            label: 'Ignora',
            variant: 'secondary',
            handler: 'dismiss',
          },
        ],
        metadata: {
          classification: 'work',
          clientNotFound: true,
          senderEmail: '{{emailClassification.senderEmail}}',
          senderName: '{{emailClassification.senderName}}',
          suggestedCompany: '{{emailClassification.suggestedCompanyName}}',
        },
      },
      next: [],
      onError: 'continue',
      retryCount: 0,
      timeout: 5000,
    },

    // Notification: Spam
    {
      id: 'notify-spam',
      name: 'Notifica spam',
      type: 'smart_notify',
      config: {
        title: 'Possibile spam',
        message: 'Da: {{emailClassification.senderEmail}}\n{{data.subject}}',
        badge: '⚠️ Possibile spam',
        priority: 'low',
        icon: 'mail-warning',
        notifType: 'new_email',
        actions: [
          {
            id: 'mark-spam',
            label: 'Conferma spam',
            variant: 'danger',
            handler: 'markAsSpam',
            payload: { senderEmail: '{{emailClassification.senderEmail}}' },
          },
          {
            id: 'open',
            label: 'Vedi comunque',
            variant: 'secondary',
            handler: 'openEmail',
            payload: { emailId: '{{entityId}}' },
          },
        ],
        metadata: {
          classification: 'spam',
          reason: '{{emailClassification.reason}}',
          senderEmail: '{{emailClassification.senderEmail}}',
        },
      },
      next: [],
      onError: 'continue',
      retryCount: 0,
      timeout: 5000,
    },

    // Notification: Advertisement
    {
      id: 'notify-advertisement',
      name: 'Notifica pubblicità',
      type: 'smart_notify',
      config: {
        title: 'Pubblicità',
        message: '{{data.subject}}',
        badge: '📢 Pubblicità',
        priority: 'low',
        icon: 'mail-ad',
        notifType: 'new_email',
        actions: [
          {
            id: 'open',
            label: 'Apri',
            variant: 'secondary',
            handler: 'openEmail',
            payload: { emailId: '{{entityId}}' },
          },
          {
            id: 'dismiss',
            label: 'Ignora',
            variant: 'secondary',
            handler: 'dismiss',
          },
        ],
        metadata: {
          classification: 'advertisement',
          reason: '{{emailClassification.reason}}',
          senderEmail: '{{emailClassification.senderEmail}}',
        },
      },
      next: [],
      onError: 'continue',
      retryCount: 0,
      timeout: 5000,
    },

    // Notification: Generic (personal or unknown)
    {
      id: 'notify-generic',
      name: 'Notifica generica',
      type: 'smart_notify',
      config: {
        title: 'Nuova email',
        message: 'Da: {{emailClassification.senderEmail}}\n{{data.subject}}',
        badge: '📬 Nuova email',
        priority: 'normal',
        icon: 'mail',
        notifType: 'new_email',
        actions: [
          {
            id: 'open',
            label: 'Apri',
            variant: 'primary',
            handler: 'openEmail',
            payload: { emailId: '{{entityId}}' },
          },
        ],
        metadata: {
          classification: '{{emailClassification.classification}}',
          reason: '{{emailClassification.reason}}',
          senderEmail: '{{emailClassification.senderEmail}}',
        },
      },
      next: [],
      onError: 'continue',
      retryCount: 0,
      timeout: 5000,
    },
  ],
  enabled: true,
  version: 1,
  tags: ['email', 'ai', 'classification', 'notifications'],
  entityType: 'email',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

/**
 * Install the email classification workflow into the registry
 */
export async function installEmailClassificationWorkflow(): Promise<void> {
  const { workflowRegistry } = await import('../workflowRegistry');
  workflowRegistry.add(emailClassificationWorkflow);
  console.log('[EmailClassificationWorkflow] Installed successfully');
}

/**
 * Uninstall the email classification workflow
 */
export async function uninstallEmailClassificationWorkflow(): Promise<void> {
  const { workflowRegistry } = await import('../workflowRegistry');
  workflowRegistry.remove(emailClassificationWorkflow.id);
  console.log('[EmailClassificationWorkflow] Uninstalled');
}
